import React, { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControl,
    IconButton,
    InputLabel,
    MenuItem,
    Select,
    TextField,
    useTheme,
} from '@material-ui/core'
import CloseIcon from '@material-ui/icons/Close'
import AssignmentIcon from '@material-ui/icons/Assignment'
import EditIcon from '@material-ui/icons/Edit'
import CheckIcon from '@material-ui/icons/Check'
import SaveIcon from '@material-ui/icons/Save'
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline'
import { Container } from '../../components'
import { vendorProjectApi } from '../../contexts'
import {
    ALL_VENDOR_TASK_STATUSES,
    CreateTaskData,
    UpdateTaskData,
    VENDOR_TASK_STATUS_COLORS,
    VENDOR_TASK_STATUS_LABELS,
    VendorProject,
    VendorTask,
    VendorTaskStatus,
} from '../../models'
import { useUser } from '../../hooks'
import { typographyStyle } from '../../theme/typographyStyle'
import { RootState } from '../../store'
import { fromDateInput, toDateInput } from './taskDateHelpers'

interface Props {
    open: boolean
    project: VendorProject
    task?: VendorTask
    onClose: () => void
}

interface TaskFormState {
    title: string
    description: string
    assigned_to_id: number | ''
    schedule_time: string
    address: string
    status: VendorTaskStatus
}

const blankForm = (project: VendorProject): TaskFormState => ({
    title: '',
    description: '',
    assigned_to_id: '',
    schedule_time: '',
    address: project.address ?? '',
    status: 'ASSIGNED',
})

const taskToForm = (
    task: VendorTask,
    project: VendorProject,
): TaskFormState => ({
    title: task.title ?? '',
    description: task.description ?? '',
    assigned_to_id: task.assigned_to?.id ?? task.assigned_to_id ?? '',
    schedule_time: toDateInput(task.schedule_time),
    address: task.address ?? project.address ?? '',
    status: task.status,
})

export const VendorTaskForm = (props: Props) => {
    const { open, project, task, onClose } = props
    const theme = useTheme()
    const isEdit = !!task
    const { workspaceUser } = useUser()
    const userList = useSelector((s: RootState) => s.user.userList)
    const { createProjectTask, updateProjectTask } = vendorProjectApi()

    const [form, setForm] = useState<TaskFormState>(blankForm(project))
    const [errors, setErrors] = useState<
        Partial<Record<keyof TaskFormState, string>>
    >({})
    const [submitting, setSubmitting] = useState(false)
    const [submitError, setSubmitError] = useState<string | null>(null)

    useEffect(() => {
        if (!open) return
        if (task) {
            setForm(taskToForm(task, project))
        } else {
            const next = blankForm(project)
            if (workspaceUser?.id) {
                next.assigned_to_id = workspaceUser.id
            }
            setForm(next)
        }
        setErrors({})
        setSubmitError(null)
    }, [open, task, project, workspaceUser?.id])

    const validate = (): boolean => {
        const next: Partial<Record<keyof TaskFormState, string>> = {}
        if (!form.title.trim()) next.title = 'Title is required'
        if (!form.assigned_to_id) next.assigned_to_id = 'Assignee is required'
        setErrors(next)
        return Object.keys(next).length === 0
    }

    const handleSubmit = async () => {
        if (!validate()) return
        setSubmitting(true)
        setSubmitError(null)
        try {
            if (isEdit && task) {
                const body: UpdateTaskData = {
                    title: form.title,
                    description: form.description || null,
                    assigned_to_id: form.assigned_to_id as number,
                    schedule_time: fromDateInput(form.schedule_time) ?? null,
                    address: form.address || null,
                    status: form.status,
                }
                await updateProjectTask(task.id, body)
            } else {
                const body: CreateTaskData = {
                    title: form.title,
                    description: form.description || null,
                    assigned_to_id: form.assigned_to_id as number,
                    schedule_time: fromDateInput(form.schedule_time) ?? null,
                    address: form.address || null,
                }
                await createProjectTask(body, project.id)
            }
            // Only close when the API call succeeded - on failure the context
            // throws and we show the error inline so the user can fix it.
            onClose()
        } catch (e) {
            const msg =
                e instanceof Error && e.message
                    ? e.message
                    : 'Something went wrong. Please try again.'
            setSubmitError(msg)
        } finally {
            setSubmitting(false)
        }
    }

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle disableTypography style={{ paddingBottom: 0 }}>
                <Container
                    alignItems="center"
                    style={{ gap: theme.spacing(1) }}
                >
                    <Container
                        alignItems="center"
                        justifyContent="center"
                        style={{
                            width: 36,
                            height: 36,
                            borderRadius: 8,
                            backgroundColor: '#e8efef',
                            color: theme.palette.brand1.main,
                        }}
                    >
                        {isEdit ? <EditIcon /> : <AssignmentIcon />}
                    </Container>
                    <Container
                        direction="column"
                        flex={1}
                        style={{ minWidth: 0 }}
                    >
                        <Container
                            style={{
                                ...typographyStyle(theme.typography.h6),
                                fontWeight: 700,
                            }}
                        >
                            {isEdit ? 'Edit Task' : 'Add Task'}
                        </Container>
                        <Container
                            style={{
                                ...typographyStyle(theme.typography.caption),
                                color: theme.palette.text.secondary,
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                            }}
                        >
                            {project.title}
                        </Container>
                    </Container>
                    <IconButton size="small" onClick={onClose}>
                        <CloseIcon />
                    </IconButton>
                </Container>
            </DialogTitle>
            <Divider style={{ margin: theme.spacing(2, 3, 0) }} />
            <DialogContent>
                <Container
                    direction="column"
                    style={{ gap: theme.spacing(2.5) }}
                >
                    {submitError && (
                        <Container
                            alignItems="flex-start"
                            style={{
                                gap: theme.spacing(1),
                                padding: theme.spacing(1.25, 1.5),
                                borderRadius: 8,
                                border: `1px solid ${theme.palette.error.light}`,
                                backgroundColor: '#ffebee',
                                color: theme.palette.error.dark,
                            }}
                        >
                            <ErrorOutlineIcon
                                fontSize="small"
                                style={{ marginTop: 2 }}
                            />
                            <Container
                                direction="column"
                                flex={1}
                                style={{
                                    ...typographyStyle(theme.typography.body2),
                                }}
                            >
                                <span style={{ fontWeight: 700 }}>
                                    {isEdit
                                        ? 'Could not update task'
                                        : 'Could not create task'}
                                </span>
                                <span>{submitError}</span>
                            </Container>
                        </Container>
                    )}
                    <TextField
                        label="Title"
                        value={form.title}
                        onChange={(e) =>
                            setForm({ ...form, title: e.target.value })
                        }
                        required
                        error={!!errors.title}
                        helperText={errors.title}
                        variant="outlined"
                        size="small"
                        fullWidth
                    />

                    <TextField
                        label="Description"
                        value={form.description}
                        onChange={(e) =>
                            setForm({
                                ...form,
                                description: e.target.value,
                            })
                        }
                        multiline
                        rows={2}
                        variant="outlined"
                        fullWidth
                    />

                    <Container style={{ gap: theme.spacing(2) }}>
                        <FormControl
                            fullWidth
                            required
                            error={!!errors.assigned_to_id}
                            variant="outlined"
                            size="small"
                        >
                            <InputLabel id="vp-task-assignee-label">
                                Assignee
                            </InputLabel>
                            <Select
                                labelId="vp-task-assignee-label"
                                label="Assignee"
                                value={form.assigned_to_id}
                                onChange={(e) =>
                                    setForm({
                                        ...form,
                                        assigned_to_id: Number(e.target.value),
                                    })
                                }
                            >
                                {userList.map((u) => (
                                    <MenuItem key={u.id} value={u.id}>
                                        {u.name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        {isEdit && (
                            <FormControl
                                fullWidth
                                variant="outlined"
                                size="small"
                            >
                                <InputLabel id="vp-task-status-label">
                                    Status
                                </InputLabel>
                                <Select
                                    labelId="vp-task-status-label"
                                    label="Status"
                                    value={form.status}
                                    onChange={(e) =>
                                        setForm({
                                            ...form,
                                            status: e.target
                                                .value as VendorTaskStatus,
                                        })
                                    }
                                    renderValue={(value) => {
                                        const status = value as VendorTaskStatus
                                        const c =
                                            VENDOR_TASK_STATUS_COLORS[status]
                                        return (
                                            <Container
                                                alignItems="center"
                                                style={{ gap: 6 }}
                                            >
                                                <span
                                                    style={{
                                                        width: 8,
                                                        height: 8,
                                                        borderRadius: '50%',
                                                        backgroundColor: c.fg,
                                                        display: 'inline-block',
                                                    }}
                                                />
                                                {
                                                    VENDOR_TASK_STATUS_LABELS[
                                                        status
                                                    ]
                                                }
                                            </Container>
                                        )
                                    }}
                                >
                                    {ALL_VENDOR_TASK_STATUSES.map((s) => {
                                        const c = VENDOR_TASK_STATUS_COLORS[s]
                                        return (
                                            <MenuItem key={s} value={s}>
                                                <Container
                                                    alignItems="center"
                                                    style={{ gap: 6 }}
                                                >
                                                    <span
                                                        style={{
                                                            width: 8,
                                                            height: 8,
                                                            borderRadius: '50%',
                                                            backgroundColor:
                                                                c.fg,
                                                            display:
                                                                'inline-block',
                                                        }}
                                                    />
                                                    {
                                                        VENDOR_TASK_STATUS_LABELS[
                                                            s
                                                        ]
                                                    }
                                                </Container>
                                            </MenuItem>
                                        )
                                    })}
                                </Select>
                            </FormControl>
                        )}
                    </Container>

                    <TextField
                        label="Scheduled Date"
                        type="date"
                        value={form.schedule_time}
                        onChange={(e) =>
                            setForm({
                                ...form,
                                schedule_time: e.target.value,
                            })
                        }
                        InputLabelProps={{ shrink: true }}
                        variant="outlined"
                        size="small"
                        fullWidth
                    />

                    <TextField
                        label="Address"
                        value={form.address}
                        onChange={(e) =>
                            setForm({ ...form, address: e.target.value })
                        }
                        helperText={
                            project.address
                                ? `Defaults to project address: ${project.address}`
                                : 'Defaults to project address'
                        }
                        variant="outlined"
                        size="small"
                        fullWidth
                    />
                </Container>
            </DialogContent>
            <DialogActions
                style={{
                    padding: theme.spacing(1.5, 3),
                    backgroundColor: '#fafbfb',
                    borderTop: `1px solid ${theme.palette.divider}`,
                }}
            >
                <Container flex={1} />
                <Button
                    variant="outlined"
                    onClick={onClose}
                    disabled={submitting}
                    style={{
                        fontWeight: 600,
                        textTransform: 'none',
                        marginRight: theme.spacing(1),
                    }}
                >
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    color="primary"
                    onClick={handleSubmit}
                    disabled={submitting}
                    disableElevation
                    startIcon={isEdit ? <SaveIcon /> : <CheckIcon />}
                    style={{
                        fontWeight: 700,
                        textTransform: 'none',
                        minWidth: 160,
                        boxShadow:
                            '0 1px 2px rgba(70,176,131,0.2), 0 4px 12px rgba(70,176,131,0.15)',
                    }}
                >
                    {isEdit ? 'Save Changes' : 'Create Task'}
                </Button>
            </DialogActions>
        </Dialog>
    )
}
